import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-apollo';
import { useIntl } from 'react-intl-next';

import { LoadingButton } from '@atlaskit/button';
import CheckIcon from '@atlaskit/icon/core/check-mark';

import { Attribution, withErrorBoundary } from '@confluence/error-boundary';
import type { FlagsStateContainer } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { fg } from '@confluence/feature-gating';

import { messages } from './messages';
import { experimentalRequestAccessMutation as RequestAccessMutation } from './RequestAccessMutation.experimentalgraphql.js';
import type {
	experimentalRequestAccessMutation,
	experimentalRequestAccessMutationVariables,
} from './__types__/experimentalRequestAccessMutation';

const REQUEST_SENT_FLAG_ID = 'request-to-edit-sent';

export type RequestToEditComponentProps = {
	contentId: string | undefined | null;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
};

export const RequestToEditButtonComponent = ({
	contentId,
	flags,
	requestToEditContent,
	hasRequestedEditPermission = false,
}: RequestToEditComponentProps & { flags: FlagsStateContainer }) => {
	const [requestSent, setRequestSent] = useState(false);
	const intl = useIntl();

	useEffect(() => {
		// reset the state requestSent on the content change
		setRequestSent(false);
	}, [contentId]);

	useEffect(() => {
		if (fg('editor_request_to_edit_task')) {
			setRequestSent(hasRequestedEditPermission);
		}
	}, [hasRequestedEditPermission]);

	const showRequestSentFlag = (ownerName: string) => {
		void flags.showSuccessFlag({
			id: REQUEST_SENT_FLAG_ID,
			title: intl.formatMessage(messages.requestSentFlagTitle),
			description: intl.formatMessage(messages.requestSentFlagDescription, {
				ownerName,
			}),
			actions: [
				{
					content: intl.formatMessage(messages.requestSentFlagAction),
					onClick: () => flags.hideFlag(REQUEST_SENT_FLAG_ID),
				},
			],
		});
	};

	const showNotSentFlag = () => {
		void flags.showWarningFlag({
			title: intl.formatMessage(messages.notSentFlagTitle),
			description: intl.formatMessage(messages.notSentFlagDescription),
			appearance: 'warning',
			isAutoDismiss: true,
		});
	};

	// Once we have the success response set the state requestSent to true
	const handleRequestComplete = (result: experimentalRequestAccessMutation) => {
		if (result?.experimentalRequestAccess?.complete) {
			setRequestSent(true);
			const ownerName = result?.experimentalRequestAccess?.fullName || '';
			showRequestSentFlag(ownerName);
		} else {
			showNotSentFlag();
		}
	};

	const [requestAccess, { loading }] = useMutation<
		experimentalRequestAccessMutation,
		experimentalRequestAccessMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		RequestAccessMutation,
		{
			variables: { contentId: contentId!, accessType: 'edit' },
			onCompleted: handleRequestComplete,
			onError: showNotSentFlag,
		},
	);

	const handleClick = () => {
		if (requestToEditContent && fg('editor_request_to_edit_task')) {
			requestToEditContent();
		} else {
			void requestAccess();
		}
	};

	return (
		<LoadingButton
			isDisabled={requestSent}
			onClick={handleClick}
			isLoading={loading}
			iconAfter={requestSent ? <CheckIcon spacing="spacious" label="" /> : undefined}
		>
			{intl.formatMessage(requestSent ? messages.editRequestSent : messages.requestToEdit)}
		</LoadingButton>
	);
};

export const RequestToEdit = withErrorBoundary({
	attribution: Attribution.PERMISSIONS_EXPERIENCE,
})(withFlags(RequestToEditButtonComponent));
